import _ from 'lodash';

import {
  date as commonDate,
  finleyAssertUnreachable,
  utils as commonUtils,
} from '@finley-technologies/common';
import {
  Invoice as InvoiceTypes,
  Util,
} from '@finley-technologies/finley-types';

import {
  InvoiceComponentWithInvoiceItemsInputGQLType,
  InvoiceFragmentGQLType,
  InvoiceItemGQLType,
  InvoicePaymentInKindItemGQLType,
  InvoiceReportGQLType,
  TAccountGQLType,
  TAccountTypeGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { truncateUuid } from 'packages/common-frontend/utils';
import { ChartDateRange } from 'packages/loanProduct/ui/receivablesDashboard/utils';

// more detailed status derived from Invoice.status, due date, and target invoice date
export type InvoiceComputedStatus =
  | InvoiceTypes.InvoiceStatus
  | 'scheduled' // status === created && !!targetInvoiceDate && dueDate > currentDate + 3
  | 'dueSoon' // (status === created || status === sent) && dueDate <= currentDate + 3
  | 'overdue'; // dueDate < currentDate

export const highlightComputedStatuses: InvoiceComputedStatus[] = [
  'dueSoon',
  'overdue',
  'paid',
];
export type HighlightComputedStatus = typeof highlightComputedStatuses[number];

export type InvoiceWithComputedStatus = InvoiceFragmentGQLType & {
  computedStatus: InvoiceComputedStatus;
};

export type AccrualsByTAccountMap = {
  [tAccountId: string]: {
    tAccount: TAccountGQLType;
    projectedAccruedAmount: number;
    actualAccruedAmount: number;
    totalAccruedAmount: number;
  };
};

export const getInvoiceComputedStatus = ({
  status,
  dueDate,
  targetInvoiceDate,
}: {
  status: InvoiceTypes.InvoiceStatus;
  dueDate: Util.FullDateStr;
  targetInvoiceDate?: Util.FullDateStr;
}): InvoiceComputedStatus => {
  switch (status) {
    case 'canceled':
      return 'canceled';
    case 'paid':
      return 'paid';
    case 'created':
      if (dueDate < commonDate.currentDateStr()) {
        return 'overdue';
      }
      if (dueDate < commonDate.addDays(commonDate.currentDateStr(), 3)) {
        return 'dueSoon';
      }
      if (targetInvoiceDate) {
        return 'scheduled';
      }
      return 'created';
    default:
      return finleyAssertUnreachable(status, 'Invoice status is not supported');
  }
};

export const getInvoicesWithComputedStatus = ({
  invoices,
}: {
  invoices?: InvoiceFragmentGQLType[];
}): InvoiceWithComputedStatus[] | undefined => {
  if (!invoices) {
    return undefined;
  }

  return _.compact(
    invoices.map((invoice) => {
      const { status, dueDate, targetInvoiceDate } = invoice;
      const computedStatus = getInvoiceComputedStatus({
        status,
        dueDate,
        targetInvoiceDate: targetInvoiceDate ?? undefined,
      });

      return {
        ...invoice,
        computedStatus,
      };
    }),
  );
};

export const filterInvoicesForDatesAndCreditFacilities = ({
  invoices,
  dateRange,
  selectedCreditFacilityIds,
}: {
  invoices?: InvoiceWithComputedStatus[];
  dateRange: ChartDateRange | null;
  selectedCreditFacilityIds: string[];
}): InvoiceWithComputedStatus[] | undefined => {
  if (!invoices) {
    return undefined;
  }

  return invoices.filter((invoice) => {
    const invoiceDueDateJs = commonDate.fullDateStrToDate(invoice.dueDate);
    const includesCreditFacility = selectedCreditFacilityIds.includes(
      invoice.creditFacilityId,
    );

    if (!dateRange) {
      return includesCreditFacility;
    }

    return (
      invoiceDueDateJs >= dateRange.chartDataStartDate &&
      invoiceDueDateJs <= dateRange.chartDataEndDate &&
      includesCreditFacility
    );
  });
};

export const filterInvoicesForSelectedComputedStatus = ({
  invoices,
  selectedComputedStatus,
}: {
  invoices?: InvoiceWithComputedStatus[];
  selectedComputedStatus: HighlightComputedStatus | null;
}): InvoiceWithComputedStatus[] | undefined => {
  if (!invoices) {
    return undefined;
  }

  if (selectedComputedStatus) {
    return invoices.filter(
      (invoice) => invoice.computedStatus === selectedComputedStatus,
    );
  }

  return invoices;
};

// TAccountTypes that don't exist in this map will be labeled directly without this translation
// Can add more types to this as needed
export const TAccountTypeToLabelMap: Partial<
  Record<TAccountTypeGQLType, string>
> = {
  longTermDebt: 'Outstanding Balance',
  accruedInterest: 'Interest',
};

export const getInvoiceOverviewInitialDateRange = (): ChartDateRange => {
  const currentDate = new Date();
  const initialStartDate = new Date();
  const initialEndDate = new Date();

  initialStartDate.setDate(currentDate.getDate() - 90);
  initialEndDate.setDate(currentDate.getDate() + 90);

  return {
    chartDataStartDate: initialStartDate,
    chartDataEndDate: initialEndDate,
  };
};

export const getBankAccountDisplay = ({
  institutionName,
  accountName,
  accountNumber,
}: {
  institutionName: string;
  accountName: string;
  accountNumber: string;
}) => {
  return `${institutionName}, ${accountName} (...${accountNumber.substring(
    accountNumber.length - 4,
  )})`;
};

export const getInterestInvoiceItemLabel = ({
  invoiceItem,
  invoiceLoanId,
}: {
  invoiceItem:
    | Omit<
        InvoiceItemGQLType,
        'id' | 'invoiceId' | 'created_at' | 'updated_at' | 'accountingAccount'
      >
    | InvoiceItemGQLType;
  invoiceLoanId?: string | null;
}) => {
  if (!invoiceItem.capitalizedLoanId) {
    return 'Cash';
  }

  if (invoiceLoanId === invoiceItem.capitalizedLoanId) {
    return 'PIK to This Loan';
  }

  return `PIK to Loan ${truncateUuid(invoiceItem.capitalizedLoanId)}`;
};

export const groupPaymentInKindInvoiceReportsByCapitalizedLoanId = ({
  invoiceReports,
  invoicePaymentInKindItems,
}: {
  invoiceReports: InvoiceReportGQLType[];
  invoicePaymentInKindItems: InvoicePaymentInKindItemGQLType[];
}): Record<string, InvoiceReportGQLType[]> => {
  const paymentInKindInvoiceReports = invoiceReports.filter(
    (report) => report.reportType === 'paymentInKindNotice',
  );

  return _.groupBy(paymentInKindInvoiceReports, (report) => {
    const invoicePaymentInKindItem = invoicePaymentInKindItems.find(
      (item) => item.id === report.paymentInKindItemId,
    );

    return invoicePaymentInKindItem?.capitalizedLoanId;
  });
};

export const getUpdatedInvoiceTotal = ({
  invoiceComponentsWithInvoiceItems,
  updatedInvoiceComponentTotal,
  invoiceComponentIndex,
}: {
  invoiceComponentsWithInvoiceItems: InvoiceComponentWithInvoiceItemsInputGQLType[];
  updatedInvoiceComponentTotal: number;
  invoiceComponentIndex: number;
}) => {
  const newInvoiceTotal = invoiceComponentsWithInvoiceItems.reduce<number>(
    (acc, invoiceComponentWithInvoiceItems, idx) => {
      if (idx === invoiceComponentIndex) {
        return acc + updatedInvoiceComponentTotal;
      }
      return (
        acc + invoiceComponentWithInvoiceItems.invoiceComponent.totalAmount
      );
    },
    0,
  );

  return commonUtils.jsRound({
    value: newInvoiceTotal,
    precision: 2,
  });
};
